import React, { useState, useEffect } from "react";
import { getUsers } from "../../api";
import * as XLSX from "xlsx";
import toast, { Toaster } from 'react-hot-toast';
import { 
  UserGroupIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const AllUsersPassword = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await getUsers();
        setUsers(data);
        setFilteredUsers(data);
      } catch (error) {
        toast.error('Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.class?.toString().includes(searchTerm) ||
      user.uid?.toString().includes(searchTerm)
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const exportToExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredUsers);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
      XLSX.writeFile(workbook, `users_data_${new Date().toLocaleDateString()}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export to Excel');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                <UserGroupIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">All Users</h1>
                <p className="text-sm text-gray-500">
                  Total {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all shadow-md"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Export to Excel</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mt-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, username, class, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
            />
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Username</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Password</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Class</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">School</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Course</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Parents No.</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Address</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Exam Date</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, index) => (
                    <tr key={user.uid} className={`hover:bg-gray-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-purple-600">
                            {user.uid}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-800">{user.username}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200">
                          {user.default_password}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-800">{user.name}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          Class {user.class}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{user.school}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{user.course}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{user.parents_number}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{user.contact_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{user.address}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{user.date_of_exam}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                          user.estatus > 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.estatus > 0 ? (
                            <>
                              <CheckCircleIcon className="h-4 w-4" />
                              <span>Completed</span>
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-4 w-4" />
                              <span>Pending</span>
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <UserGroupIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          {searchTerm ? 'No users found matching your search' : 'No users available'}
                        </p>
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer with Stats */}
          {filteredUsers.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Showing <span className="font-semibold text-gray-800">{filteredUsers.length}</span> of{' '}
                  <span className="font-semibold text-gray-800">{users.length}</span> users
                </span>
                <div className="flex items-center space-x-4">
                  <span className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    <span className="text-xs">
                      Completed: {users.filter(u => u.estatus > 0).length}
                    </span>
                  </span>
                  <span className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                    <span className="text-xs">
                      Pending: {users.filter(u => u.estatus === 0).length}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllUsersPassword;
